import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table';
import { useSupplierPriceLists } from '@/api/supplier-price-lists/supplier-price-lists.hooks';
import type { SupplierPriceList } from '@/api/supplier-price-lists/supplier-price-lists.types';
import { DsSearchInput } from '@/modules/internal-v2/ds';
import { ContainedPage } from '../../shared/contained-page.component';
import { DsTable } from '../../shared/ds-table.component';

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const col = createColumnHelper<SupplierPriceList>();

const columns = [
  col.accessor('name', {
    header: 'Name',
    meta: { flex: true },
    cell: (info) => {
      const feed = info.row.original;
      return (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{info.getValue()}</span>
          {feed.deletedAt && <span className="ds-pill neg">Verwaist</span>}
        </span>
      );
    },
  }),
  col.accessor('supplierBpId', {
    header: 'BP-ID',
    size: 70,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('transport', {
    header: 'Transport',
    size: 120,
    cell: (info) => {
      const t = info.getValue();
      if (t.kind === 'ftp') return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>FTP {t.host}</span>;
      if (t.kind === 'sftp') return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>SFTP {t.host}</span>;
      return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-2)' }}>{t.method ?? 'GET'} {new URL(t.url).hostname}</span>;
    },
    enableSorting: false,
  }),
  col.accessor('format', {
    header: 'Format',
    size: 65,
    cell: (info) => (
      <span className="ds-pill muted" style={{ textTransform: 'uppercase' }}>{info.getValue().kind}</span>
    ),
    enableSorting: false,
  }),
  col.accessor('enabled', {
    header: 'Aktiv',
    size: 55,
    cell: (info) => (
      <span className={`ds-pill ${info.getValue() ? 'pos' : 'muted'}`}>
        {info.getValue() ? 'Ja' : 'Nein'}
      </span>
    ),
  }),
  col.accessor('scheduleCron', {
    header: 'Cron',
    size: 90,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: v ? 'var(--ink-2)' : 'var(--ink-4)' }}>
          {v || 'Manuell'}
        </span>
      );
    },
  }),
  col.accessor('lastRunAt', {
    header: 'Letzter Lauf',
    size: 100,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-2)' }}>
        {fmtDate(info.getValue())}
      </span>
    ),
    sortingFn: (a, b) => {
      const da = a.original.lastRunAt ? new Date(a.original.lastRunAt).getTime() : 0;
      const db = b.original.lastRunAt ? new Date(b.original.lastRunAt).getTime() : 0;
      return da - db;
    },
  }),
];

interface SupplierFeedListProps {
  onSelect: (id: string) => void;
}

export function SupplierFeedList({ onSelect }: SupplierFeedListProps) {
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);

  // Feeds are code-defined; we still surface orphans (rows in DB that are no
  // longer in code) so operators can see what got disabled on last boot.
  const { data, isPending } = useSupplierPriceLists({ includeDeleted: true });

  const filtered = useMemo(() => {
    const list = data?.data ?? [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        String(f.supplierBpId).includes(q),
    );
  }, [data, search]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableSortingRemoval: false,
  });

  return (
    <ContainedPage
      contained
      toolbar={
        <>
          <DsSearchInput
            value={search}
            onSearch={setSearch}
            debounce={0}
            placeholder="Name, BP-ID…"
            style={{ maxWidth: 280 }}
          />
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 'var(--fs-xs)',
              color: 'var(--ink-3)',
            }}
          >
            Feeds werden im Code definiert (
            <code style={{ fontFamily: 'var(--font-mono)' }}>
              supplier-price-lists/config/feeds.config.ts
            </code>
            ).
          </span>
        </>
      }
    >
      <DsTable
        table={table}
        fixedLayout
        onRowClick={(row) => onSelect(row.id)}
        emptyMessage={isPending ? 'Laden…' : 'Keine Feeds gefunden.'}
      />
    </ContainedPage>
  );
}
